<template>
  <div class="videoplayer" :class="{ rounded: !props.notRounded }">
    <video
      ref="video"
      class="video"
      :class="[orientation]"
      preload="metadata"
      :controls="!showOverlay"
      disablepictureinpicture
      controlslist="nodownload"
      :src="mediaUrl"
      :type="getMediaType"
      :aria-label="label"
    />
    <div class="overlay" :class="{ show: showOverlay }">
      <button class="playpause" :aria-label="t('message.media.video.playpausebutton')" @click="overlayClick">
        <transition-group name="fade">
          <template v-if="showOverlay">
            <div class="play-button-backdrop" />
            <IconPlay width="32" height="32" />
          </template>
        </transition-group>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue';
import { computed } from 'vue';
import IconPlay from '@/svg/IconPlay.vue';
import { useI18n } from '@/composables/useI18n';
const { t } = useI18n();


const props = defineProps<{
  mediaUrl: string;
  /* mediaType: string; */
  label?: string;
  details?: string;
  notRounded?: boolean;
}>();

const showOverlay = ref(true);
const video = useTemplateRef('video');
const orientation = ref('landscape');
onMounted(() => {
  video.value?.addEventListener('loadedmetadata', () => {
    if (video.value) {
      orientation.value = video.value.videoWidth > video.value.videoHeight ? 'landscape' : 'portrait';
    }
  });

});

const getMediaType = computed(() => {
  const url = props.mediaUrl;
  const fileEnding = url.split('.').pop();
  switch (fileEnding) {
    case 'webm':
      return 'video/webm';
    case 'mov':
      return 'video/quicktime';
    case 'mp4':
      return 'video/mp4';
    default:
      return '';
  }
});

const overlayClick = () => {
  if (!video.value) return;
  const videoElement = video.value;
  if (video.value.paused) {
    videoElement.play();
    showOverlay.value = false;
  } else {
    videoElement.pause();
    showOverlay.value = true;
  }
};
</script>

<style lang="scss" scoped>
.videoplayer {
  position: relative;
  line-height: 0;
  overflow: hidden;
  max-width: 100%;
  max-height: 100%;
  height: 100%;
}

.videoplayer.rounded .overlay,
.videoplayer.rounded {
  border-radius: var(--kw-widget-global-borderradius-outer);
}

.video {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;

  &.landscape {
    width: 100%;
    height: auto;
  }

  &.portrait {
    width: auto;
    height: 100%;
  }
}

.overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background: #33333399;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: rgba(0, 0, 0, 0);
  z-index: 900;
  transition: all 0.15s;
  pointer-events: none;

  .play-button-backdrop {
    background-color: rgba(255, 255, 255, .6);
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: block;
    position: absolute;
    transition: transform 0.25s, background-color 0.25s;
    z-index: -1;
    // TODO Fix backdrop blur?
    //backdrop-filter: blur(8px);
  }

  &:hover {

    .playpause .play-button-backdrop {
      transform: scale(1.15);
      transition: transform 0.25s, background-color 0.25s;
      background-color: rgba(255, 255, 255, 1);
    }
  }

  &.show {
    transition: background-color 0.15s;
    background-color: rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(4px);
    pointer-events: all;
  }

  .playpause {
    display: flex;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    width: 50%;
    height: 50%;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    z-index: 1000;
    transform: scale(1);
    transition: transform 0.25s;
    background: none;
    border: none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
